import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { LayoutDashboard, Library, Workflow, Dumbbell, LogOut, Hexagon, CreditCard, Sparkles, Globe } from "lucide-react";

const ROLE_LABEL = { admin: "Admin", coach: "Coach", user: "Member", individual: "Individual", platform_admin: "Platform Admin" };

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/library", label: "Techniques", icon: Library, testid: "nav-library" },
  { to: "/sequences", label: "Sequences", icon: Workflow, testid: "nav-sequences" },
  { to: "/gym", label: "My Gym", icon: Dumbbell, testid: "nav-gym" },
  { to: "/billing", label: "Billing", icon: CreditCard, testid: "nav-billing" },
];

// Platform admins manage only the platform library — no gym or billing.
const PLATFORM_NAV = [
  { to: "/platform", label: "Platform", icon: Globe, testid: "nav-platform" },
  { to: "/library", label: "Techniques", icon: Library, testid: "nav-library" },
  { to: "/sequences", label: "Sequences", icon: Workflow, testid: "nav-sequences" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const initials = (user?.name || user?.email || "?").slice(0, 2).toUpperCase();
  const nav = user?.role === "platform_admin" ? PLATFORM_NAV : NAV;

  // Billing gate: once trial/subscription has lapsed, keep the user on /billing.
  const requiresPayment = user?.billing?.requires_payment;
  useEffect(() => {
    if (requiresPayment && location.pathname !== "/billing") {
      navigate("/billing", { replace: true });
    }
  }, [requiresPayment, location.pathname, navigate]);

  const handleLogout = async () => { await logout(); navigate("/login", { replace: true }); };

  const trialDays = user?.billing?.trial_days_left ?? 0;
  const showTrialBanner = user?.billing?.trial_active && location.pathname !== "/billing";

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 flex-col fixed inset-y-0 border-r border-border bg-[#0c0c0c] z-40">
        <div className="h-16 flex items-center gap-2.5 px-6 border-b border-border">
          <Hexagon className="h-6 w-6 text-primary fill-primary/20" strokeWidth={2.5} />
          <span className="font-heading font-extrabold text-lg tracking-tight">INFINITE SEQUENCE</span>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive ? "bg-primary/15 text-primary" : "text-muted-foreground hover:text-white hover:bg-white/5"
                }`
              }>
              <n.icon className="h-[18px] w-[18px]" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-border">
          <div className="flex items-center gap-3 px-2 py-2">
            <Avatar className="h-9 w-9">
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="bg-primary/20 text-primary text-xs font-semibold">{initials}</AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate">{user?.name || "Athlete"}</p>
              <p className="text-xs text-primary">{ROLE_LABEL[user?.role] || "Member"}</p>
            </div>
            <Button data-testid="logout-btn" variant="ghost" size="icon" onClick={handleLogout}
              className="text-muted-foreground hover:text-primary h-8 w-8">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 h-14 bg-black/80 backdrop-blur-xl border-b border-border z-40 flex items-center justify-between px-4">
        <div className="flex items-center gap-2"><Hexagon className="h-5 w-5 text-primary" /><span className="font-heading font-bold">INFINITE SEQUENCE</span></div>
        <Button variant="ghost" size="icon" onClick={handleLogout} className="text-muted-foreground"><LogOut className="h-4 w-4" /></Button>
      </div>

      <main className="flex-1 md:ml-64 pt-14 md:pt-0 min-h-[100dvh] flex flex-col">
        {showTrialBanner && (
          <div data-testid="trial-banner" className="shrink-0 flex items-center justify-center gap-3 px-4 py-2 bg-primary/15 border-b border-primary/30 text-sm">
            <Sparkles className="h-4 w-4 text-primary shrink-0" />
            <span className="text-foreground/90">
              <span className="font-semibold text-white">{trialDays} day{trialDays === 1 ? "" : "s"}</span> left in your free trial
            </span>
            <NavLink to="/billing" data-testid="trial-banner-cta" className="font-semibold text-primary hover:underline">Subscribe →</NavLink>
          </div>
        )}
        <Outlet />
      </main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 h-16 bg-black/90 backdrop-blur-xl border-t border-border z-40 flex">
        {nav.map((n) => (
          <NavLink key={n.to} to={n.to}
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center justify-center gap-1 text-[10px] ${isActive ? "text-primary" : "text-muted-foreground"}`
            }>
            <n.icon className="h-5 w-5" /> {n.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
